import { useState, useRef, useCallback, useEffect } from 'react';
import { 
  Bold, Italic, Underline, Strikethrough, AlignLeft, AlignCenter, AlignRight,
  List, ListOrdered, Minus, Plus, Type, Palette, Highlighter, ChevronDown,
  StickyNote, X, Maximize2, Minimize2, Trash2
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const FONT_SIZES = [8, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32];
const FONTS = ['Arial', 'Georgia', 'Times New Roman', 'Courier New', 'Verdana', 'Trebuchet MS'];
const TEXT_COLORS = [
  '#000000', '#FFFFFF', '#FF0000', '#00FF00', '#0000FF', '#FFFF00', 
  '#FF00FF', '#00FFFF', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4',
  '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F'
];
const HIGHLIGHT_COLORS = [
  'transparent', '#FFFF00', '#00FF00', '#00FFFF', '#FF00FF', '#FFA07A',
  '#E6E6FA', '#F0E68C', '#90EE90', '#FFB6C1', '#B0E0E6', '#DDA0DD'
];

const ALLOWED_NOTE_TAGS = new Set([
  'B', 'I', 'U', 'S', 'STRIKE', 'STRONG', 'EM', 'SPAN', 'DIV', 'P', 'BR', 'UL', 'OL', 'LI', 'FONT'
]);
const ALLOWED_NOTE_STYLES = new Set([
  'color', 'backgroundColor', 'background', 'fontSize', 'fontFamily', 'textAlign', 'textDecoration'
]);

function sanitizeNoteContent(html = '') {
  if (typeof window === 'undefined') return '';

  const template = document.createElement('template');
  template.innerHTML = html;

  const cleanNode = (node) => {
    if (node.nodeType === Node.COMMENT_NODE) {
      node.remove();
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return;

    if (!ALLOWED_NOTE_TAGS.has(node.tagName)) {
      node.replaceWith(...Array.from(node.childNodes));
      return;
    }

    Array.from(node.attributes).forEach((attr) => {
      const name = attr.name.toLowerCase();
      if (name !== 'style' && name !== 'color' && name !== 'face' && name !== 'size') {
        node.removeAttribute(attr.name);
      }
      if (name.startsWith('on') || /javascript:/i.test(attr.value)) {
        node.removeAttribute(attr.name);
      }
    });

    if (node.hasAttribute('style')) {
      const allowed = {};
      ALLOWED_NOTE_STYLES.forEach((styleName) => {
        const value = node.style[styleName];
        if (value && !/url\s*\(|expression\s*\(|javascript:/i.test(value)) {
          allowed[styleName] = value;
        }
      });
      node.removeAttribute('style');
      Object.entries(allowed).forEach(([styleName, value]) => {
        node.style[styleName] = value;
      });
    }
  };

  const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_COMMENT);
  const nodes = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);
  nodes.forEach(cleanNode);

  return template.innerHTML;
}

function QuickNotes({ chapterId, initialNotes = [], onSave }) {
  const editorRef = useRef(null);
  const [notes, setNotes] = useState(initialNotes);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [activeNote, setActiveNote] = useState(null);
  const [showFontPicker, setShowFontPicker] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showHighlightPicker, setShowHighlightPicker] = useState(false);
  const [currentFontSize, setCurrentFontSize] = useState(14);
  const [currentFont, setCurrentFont] = useState('Arial');
  const [showFormatting, setShowFormatting] = useState(false);

  // Initialize with a default note if empty
  useEffect(() => {
    if (notes.length === 0) {
      const defaultNote = {
        id: `note_${Date.now()}`,
        content: '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setNotes([defaultNote]);
      setActiveNote(defaultNote.id);
    } else if (!activeNote) {
      setActiveNote(notes[0].id);
    }
  }, [notes, activeNote]);

  // Save notes to parent component
  const saveNotes = useCallback((updatedNotes) => {
    if (onSave) {
      onSave(updatedNotes);
    }
  }, [onSave]);

  // Execute formatting command
  const execCommand = useCallback((command, value = null) => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      editorRef.current.focus();
    }
  }, []);

  // Handle note content change
  const handleContentChange = useCallback(() => {
    if (editorRef.current && activeNote) {
      const content = sanitizeNoteContent(editorRef.current.innerHTML);
      const updatedNotes = notes.map(note => 
        note.id === activeNote 
          ? { ...note, content, updatedAt: new Date().toISOString() }
          : note
      );
      setNotes(updatedNotes);
      saveNotes(updatedNotes);
    }
  }, [activeNote, notes, saveNotes]);

  // Add new note
  const addNote = useCallback(() => {
    const newNote = {
      id: `note_${Date.now()}`,
      content: '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    const updatedNotes = [...notes, newNote];
    setNotes(updatedNotes);
    setActiveNote(newNote.id);
    saveNotes(updatedNotes);
  }, [notes, saveNotes]);

  // Delete note
  const deleteNote = useCallback((noteId) => {
    if (notes.length <= 1) return; // Keep at least one note
    const updatedNotes = notes.filter(note => note.id !== noteId);
    setNotes(updatedNotes);
    if (activeNote === noteId) {
      setActiveNote(updatedNotes[0]?.id || null);
    }
    saveNotes(updatedNotes);
  }, [notes, activeNote, saveNotes]);

  // Get active note content
  const getActiveNoteContent = useCallback(() => {
    return notes.find(note => note.id === activeNote)?.content || '';
  }, [notes, activeNote]);

  // Set editor content when active note changes
  useEffect(() => {
    if (editorRef.current && activeNote) {
      const noteContent = sanitizeNoteContent(getActiveNoteContent());
      if (editorRef.current.innerHTML !== noteContent) {
        editorRef.current.innerHTML = noteContent;
      }
    }
  }, [activeNote, getActiveNoteContent]);

  // Formatting toolbar component
  const FormattingToolbar = () => (
    <div className="bg-[#2A2420] dark:bg-[#1A1816] rounded-lg p-2 space-y-2">
      {/* Row 1: Basic Formatting */}
      <div className="flex items-center space-x-1 flex-wrap">
        <button
          onClick={() => execCommand('bold')}
          data-testid="note-format-bold"
          className="p-2 hover:bg-gray-700 rounded text-white"
          title="Bold"
        >
          <Bold className="w-4 h-4" />
        </button>
        <button
          onClick={() => execCommand('italic')}
          data-testid="note-format-italic"
          className="p-2 hover:bg-gray-700 rounded text-white"
          title="Italic"
        >
          <Italic className="w-4 h-4" />
        </button>
        <button
          onClick={() => execCommand('underline')}
          data-testid="note-format-underline"
          className="p-2 hover:bg-gray-700 rounded text-white"
          title="Underline"
        >
          <Underline className="w-4 h-4" />
        </button>
        <button
          onClick={() => execCommand('strikeThrough')}
          data-testid="note-format-strikethrough"
          className="p-2 hover:bg-gray-700 rounded text-white"
          title="Strikethrough"
        >
          <Strikethrough className="w-4 h-4" />
        </button>
        
        <div className="w-px h-6 bg-gray-600 mx-1" />
        
        <button
          onClick={() => execCommand('justifyLeft')}
          className="p-2 hover:bg-gray-700 rounded text-white"
          title="Align Left"
        >
          <AlignLeft className="w-4 h-4" />
        </button>
        <button
          onClick={() => execCommand('justifyCenter')}
          className="p-2 hover:bg-gray-700 rounded text-white"
          title="Align Center"
        >
          <AlignCenter className="w-4 h-4" />
        </button>
        <button
          onClick={() => execCommand('justifyRight')}
          className="p-2 hover:bg-gray-700 rounded text-white"
          title="Align Right"
        >
          <AlignRight className="w-4 h-4" />
        </button>
        
        <div className="w-px h-6 bg-gray-600 mx-1" />
        
        <button
          onClick={() => execCommand('insertUnorderedList')}
          className="p-2 hover:bg-gray-700 rounded text-white"
          title="Bullet List"
        >
          <List className="w-4 h-4" />
        </button>
        <button
          onClick={() => execCommand('insertOrderedList')}
          className="p-2 hover:bg-gray-700 rounded text-white"
          title="Numbered List"
        >
          <ListOrdered className="w-4 h-4" />
        </button>
      </div>

      {/* Row 2: Font Size Control */}
      <div className="flex items-center space-x-2 border-t border-gray-700 pt-2">
        <div className="flex items-center bg-gray-700 rounded">
          <button
            onClick={() => {
              const newSize = Math.max(8, currentFontSize - 2);
              setCurrentFontSize(newSize);
              execCommand('fontSize', '3');
              const selection = window.getSelection();
              if (selection.rangeCount > 0) {
                const span = document.createElement('span');
                span.style.fontSize = `${newSize}px`;
              }
            }}
            className="p-1.5 hover:bg-gray-600 rounded-l text-white"
            title="Decrease Size"
          >
            <Minus className="w-3 h-3" />
          </button>
          <span className="px-2 text-sm text-white min-w-[40px] text-center">{currentFontSize}pt</span>
          <button
            onClick={() => {
              const newSize = Math.min(32, currentFontSize + 2);
              setCurrentFontSize(newSize);
            }}
            className="p-1.5 hover:bg-gray-600 rounded-r text-white"
            title="Increase Size"
          >
            <Plus className="w-3 h-3" />
          </button>
        </div>

        {/* Font Picker */}
        <div className="relative">
          <button
            onClick={() => setShowFontPicker(!showFontPicker)}
            className="flex items-center space-x-1 bg-gray-700 hover:bg-gray-600 rounded px-2 py-1.5 text-white text-sm"
          >
            <Type className="w-4 h-4" />
            <span className="max-w-[80px] truncate">{currentFont}</span>
            <ChevronDown className="w-3 h-3" />
          </button>
          {showFontPicker && (
            <div className="absolute top-full left-0 mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-xl z-50 py-1 min-w-[150px]">
              {FONTS.map(font => (
                <button
                  key={font}
                  onClick={() => {
                    setCurrentFont(font);
                    execCommand('fontName', font);
                    setShowFontPicker(false);
                  }}
                  className={`w-full text-left px-3 py-1.5 hover:bg-gray-700 text-white text-sm ${
                    currentFont === font ? 'bg-gray-700' : ''
                  }`}
                  style={{ fontFamily: font }}
                >
                  {font}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Row 3: Color Pickers */}
      <div className="flex items-center space-x-2 border-t border-gray-700 pt-2">
        {/* Text Color */}
        <div className="relative">
          <button
            onClick={() => {
              setShowColorPicker(!showColorPicker);
              setShowHighlightPicker(false);
            }}
            className="flex items-center space-x-1 bg-gray-700 hover:bg-gray-600 rounded px-2 py-1.5 text-white text-sm"
            title="Text Color"
          >
            <Palette className="w-4 h-4" />
            <span>Text</span>
          </button>
          {showColorPicker && (
            <div className="absolute top-full left-0 mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-xl z-50 p-2">
              <div className="grid grid-cols-4 gap-1">
                {TEXT_COLORS.map(color => (
                  <button
                    key={color}
                    onClick={() => {
                      execCommand('foreColor', color);
                      setShowColorPicker(false);
                    }}
                    className="w-6 h-6 rounded border border-gray-500 hover:scale-110 transition-transform"
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Highlight Color */}
        <div className="relative">
          <button
            onClick={() => {
              setShowHighlightPicker(!showHighlightPicker);
              setShowColorPicker(false);
            }}
            className="flex items-center space-x-1 bg-gray-700 hover:bg-gray-600 rounded px-2 py-1.5 text-white text-sm"
            title="Highlight Color"
          >
            <Highlighter className="w-4 h-4" />
            <span>Highlight</span>
          </button>
          {showHighlightPicker && (
            <div className="absolute top-full left-0 mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-xl z-50 p-2">
              <div className="grid grid-cols-4 gap-1">
                {HIGHLIGHT_COLORS.map((color, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      execCommand('hiliteColor', color);
                      setShowHighlightPicker(false);
                    }}
                    className="w-6 h-6 rounded border border-gray-500 hover:scale-110 transition-transform"
                    style={{ backgroundColor: color === 'transparent' ? 'transparent' : color }}
                    title={color === 'transparent' ? 'No Highlight' : color}
                  >
                    {color === 'transparent' && <X className="w-4 h-4 text-red-500 mx-auto" />}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (isMinimized) {
    return (
      <div className="fixed bottom-4 right-4 z-40">
        <button
          onClick={() => setIsMinimized(false)}
          data-testid="expand-notes-button"
          className="flex items-center space-x-2 rounded-full border border-[#D8CBBC] bg-[#FFFCF7] p-3 text-[#2A2420] shadow-[0_18px_42px_-28px_rgba(58,43,31,0.65)] transition hover:bg-[#F7F1E9] dark:border-[#3A332C] dark:bg-[#211E1A] dark:text-[#F0EBE4] dark:hover:bg-[#29241F]"
        >
          <StickyNote className="w-5 h-5" />
          <span className="font-medium pr-1">Notes ({notes.length})</span>
        </button>
      </div>
    );
  }

  return (
    <div 
      className={`rounded-2xl border border-[#E7DED1]/80 bg-[#FFFCF7]/82 text-[#2A2420] shadow-[0_18px_42px_-36px_rgba(58,43,31,0.55)] backdrop-blur-sm transition-all duration-300 dark:border-[#302820]/80 dark:bg-[#211E1A]/82 dark:text-[#F0EBE4] ${
        isExpanded ? 'fixed inset-4 z-50' : ''
      }`}
      data-testid="quick-notes-container"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#ECE3D7]/80 dark:border-[#302820]/70">
        <div className="flex items-center space-x-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-full border border-[#D8CBBC] bg-[#F7F0E8] text-[#756958] dark:border-[#3A332C] dark:bg-[#29241F] dark:text-[#B8AEA4]">
            <StickyNote className="w-4 h-4" />
          </span>
          <h3 className="text-sm font-semibold text-[#2A2420] dark:text-[#F0EBE4]">Notes</h3>
          <span className="rounded-full border border-[#E3D8CA] bg-[#F7F1E9] px-2 py-0.5 text-[11px] text-[#6F655C] dark:border-[#3A332C] dark:bg-[#29241F] dark:text-[#B8AEA4]">
            {notes.length}
          </span>
        </div>
        <div className="flex items-center space-x-1">
          <button
            onClick={() => setShowFormatting(!showFormatting)}
            className={`p-1.5 rounded-lg transition hover:bg-[#F7F1E9] dark:hover:bg-[#29241F] ${
              showFormatting ? 'bg-[#F7F1E9] dark:bg-[#29241F]' : ''
            }`}
            title="Toggle Formatting"
            data-testid="toggle-formatting-button"
          >
            <Type className="w-4 h-4 text-[#6F655C] dark:text-[#B8AEA4]" />
          </button>
          <button
            onClick={addNote}
            className="p-1.5 rounded-lg transition hover:bg-[#F7F1E9] dark:hover:bg-[#29241F]"
            title="Add Note"
            data-testid="add-note-button"
          >
            <Plus className="w-4 h-4 text-[#6F655C] dark:text-[#B8AEA4]" />
          </button>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1.5 rounded-lg transition hover:bg-[#F7F1E9] dark:hover:bg-[#29241F]"
            title={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? (
              <Minimize2 className="w-4 h-4 text-[#6F655C] dark:text-[#B8AEA4]" />
            ) : (
              <Maximize2 className="w-4 h-4 text-[#6F655C] dark:text-[#B8AEA4]" />
            )}
          </button>
          <button
            onClick={() => setIsMinimized(true)}
            className="p-1.5 rounded-lg transition hover:bg-[#F7F1E9] dark:hover:bg-[#29241F]"
            title="Minimize"
            data-testid="minimize-notes-button"
          >
            <Minus className="w-4 h-4 text-[#6F655C] dark:text-[#B8AEA4]" />
          </button>
        </div>
      </div>

      {/* Note Tabs */}
      {notes.length > 1 && (
        <div className="flex items-center space-x-1 px-2 py-2 border-b border-[#ECE3D7]/80 dark:border-[#302820]/70 overflow-x-auto">
          {notes.map((note, idx) => (
            <div key={note.id} className="flex items-center">
              <button
                onClick={() => setActiveNote(note.id)}
                className={`px-3 py-1 text-sm rounded-t transition-colors ${
                  activeNote === note.id
                    ? 'bg-[#F7F1E9] dark:bg-[#29241F] text-[#2A2420] dark:text-[#F0EBE4] font-medium'
                    : 'text-[#6F655C] dark:text-[#B8AEA4] hover:bg-[#F7F1E9]/70 dark:hover:bg-[#29241F]/70'
                }`}
              >
                Note {idx + 1}
              </button>
              {notes.length > 1 && (
                <button
                  onClick={() => deleteNote(note.id)}
                  className="p-1 hover:bg-red-100 dark:hover:bg-red-900/20 rounded ml-1"
                  title="Delete Note"
                >
                  <X className="w-3 h-3 text-red-500" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Formatting Toolbar */}
      {showFormatting && (
        <div className="p-2 border-b border-[#ECE3D7]/80 dark:border-[#302820]/70">
          <FormattingToolbar />
        </div>
      )}

      {/* Editor */}
      <div 
        ref={editorRef}
        contentEditable
        spellCheck={true}
        onInput={handleContentChange}
        onBlur={handleContentChange}
        data-testid="quick-notes-editor"
        className={`p-4 focus:outline-none text-[#2A2420] dark:text-[#F0EBE4] ${
          isExpanded ? 'h-[calc(100%-180px)]' : 'min-h-[200px] max-h-[400px]'
        } overflow-y-auto bg-transparent leading-relaxed`}
        style={{ fontFamily: currentFont, fontSize: `${currentFontSize}px` }}
        placeholder="Write your notes here..."
      />

      {/* Footer */}
      <div className="px-4 py-2 border-t border-[#ECE3D7]/80 dark:border-[#302820]/70 text-xs text-[#7C7065] dark:text-[#A99F95] flex justify-between">
        <span>Auto-saved</span>
        <span>
          {notes.find(n => n.id === activeNote)?.updatedAt 
            ? `Last edited: ${new Date(notes.find(n => n.id === activeNote)?.updatedAt).toLocaleTimeString()}`
            : ''
          }
        </span>
      </div>
    </div>
  );
}

export default QuickNotes;
